import React, { useMemo, useState } from 'react'
import { makeStyles, Typography, IconButton, Collapse } from '@material-ui/core'
import ErrorIcon from '@material-ui/icons/Error'
import WarningIcon from '@material-ui/icons/Warning'
import InfoIcon from '@material-ui/icons/Info'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import { CapeeshColors } from '../../../../assets/ColorPalette'
import { ScenarioNodeType } from '../../../../Logic/ScenarioConstants'

const LANGUAGE_ONLY_NODE_TYPES = new Set([
    ScenarioNodeType.Speak,
    ScenarioNodeType.TestDefinition,
    ScenarioNodeType.PronunciationSelfAssessment,
    ScenarioNodeType.WritingAiEvaluation,
])

const NODE_TYPE_NAMES = {
    [ScenarioNodeType.Watch]: 'Watch',
    [ScenarioNodeType.Listen]: 'Listen',
    [ScenarioNodeType.Speak]: 'Speak',
    [ScenarioNodeType.Debrief]: 'Debrief',
    [ScenarioNodeType.BranchingMultiChoice]: 'Branching MultiChoice',
    [ScenarioNodeType.Brief]: 'Brief',
    [ScenarioNodeType.TestComprehension]: 'Comprehension',
    [ScenarioNodeType.TestRule]: 'Rule Card',
    [ScenarioNodeType.TestListenWithSlider]: 'Listen with Slider',
    [ScenarioNodeType.TestDefinition]: 'Definition',
    [ScenarioNodeType.TestListenFillInBlank]: 'Fill in Blank',
    [ScenarioNodeType.TestImage]: 'Image Test',
    [ScenarioNodeType.TestDialog]: 'Dialog',
    [ScenarioNodeType.TestAssociationPuzzle]: 'Association Puzzle',
    [ScenarioNodeType.MultiChoice]: 'Multi Choice',
    [ScenarioNodeType.ListenMultiChoice]: 'Listen Multi Choice',
    [ScenarioNodeType.Instruction]: 'Instruction',
    [ScenarioNodeType.PronunciationSelfAssessment]: 'Pronunciation',
    [ScenarioNodeType.WritingAiEvaluation]: 'Writing',
    [ScenarioNodeType.Video]: 'Video',
}

export function validateNativeSpeakerContent(scenario, nodes, l2Id) {
    const issues = []

    if (!scenario || !nodes) return issues

    const briefNode = nodes.find((n) => n.scenarioNodeType === ScenarioNodeType.Brief)
    if (briefNode) {
        const briefOption = briefNode.options?.[0]
        if (briefOption && briefOption.textLanguageId !== l2Id && !scenario.l2Brief) {
            issues.push({
                nodeId: briefNode.nodeId,
                field: 'Brief',
                severity: 'error',
                message: 'Brief is in L1 with no L2 alternative',
            })
        }
    }

    const debriefNode = nodes.find((n) => n.scenarioNodeType === ScenarioNodeType.Debrief)
    if (debriefNode) {
        debriefNode.options?.forEach((opt, idx) => {
            const label = idx === 0 ? 'Pass feedback' : 'Fail feedback'
            if (opt.textLanguageId !== l2Id && !opt.l2Text) {
                issues.push({
                    nodeId: debriefNode.nodeId,
                    field: label,
                    severity: 'error',
                    message: `Debrief "${label}" is in L1 with no L2 alternative`,
                })
            }
        })
    }

    if (scenario.nameLanguageId !== l2Id && !scenario.l2Name) {
        issues.push({
            nodeId: null,
            field: 'Lesson name',
            severity: 'warning',
            message: 'Lesson name is in L1 without L2 alternative',
        })
    }

    if (scenario.shortDescriptionLanguageId !== l2Id && !scenario.l2ShortDescription) {
        issues.push({
            nodeId: null,
            field: 'Short description',
            severity: 'warning',
            message: 'Short description is in L1 without L2 alternative',
        })
    }

    const QUIZ_NODE_TYPES = new Set([
        ScenarioNodeType.TestComprehension,
        ScenarioNodeType.MultiChoice,
        ScenarioNodeType.ListenMultiChoice,
        ScenarioNodeType.TestImage,
    ])

    nodes.forEach((node) => {
        if (QUIZ_NODE_TYPES.has(node.scenarioNodeType)) {
            node.testOptions?.forEach((testOption) => {
                if (testOption.textLanguageId !== l2Id && !testOption.l2Text) {
                    const typeName = NODE_TYPE_NAMES[node.scenarioNodeType] || 'Quiz'
                    issues.push({
                        nodeId: node.nodeId,
                        field: `${typeName} question`,
                        severity: 'warning',
                        message: `${typeName} question is in L1 with no L2 alternative for native speakers`,
                    })
                }
            })
        }

        if (node.scenarioNodeType === ScenarioNodeType.TestAssociationPuzzle) {
            issues.push({
                nodeId: node.nodeId,
                field: 'Association Puzzle',
                severity: 'warning',
                message: 'Association Puzzle may require L1 content — verify manually',
            })
        }

        if (LANGUAGE_ONLY_NODE_TYPES.has(node.scenarioNodeType)) {
            const typeName = NODE_TYPE_NAMES[node.scenarioNodeType] || 'Unknown'
            issues.push({
                nodeId: node.nodeId,
                field: typeName,
                severity: 'info',
                message: `${typeName} stage will be skipped for native speakers`,
            })
        }
    })

    return issues
}

const useStyles = makeStyles((theme) => ({
    root: {
        margin: '8px 0',
        borderRadius: 8,
        overflow: 'hidden',
        border: `1px solid ${CapeeshColors.Grey200}`,
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 12px',
        backgroundColor: CapeeshColors.Grey50,
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: CapeeshColors.Grey100,
        },
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
    },
    headerTitle: {
        fontFamily: 'Rubik',
        fontSize: 13,
        fontWeight: 500,
        color: CapeeshColors.Gray900,
    },
    badge: {
        fontFamily: 'Rubik',
        fontSize: 11,
        fontWeight: 600,
        padding: '2px 8px',
        borderRadius: 10,
        color: '#FFFFFF',
    },
    badgeError: {
        backgroundColor: CapeeshColors.Red,
    },
    badgeWarning: {
        backgroundColor: '#F59E0B',
    },
    badgeInfo: {
        backgroundColor: CapeeshColors.BlueBright,
    },
    issueList: {
        padding: 0,
        margin: 0,
        listStyle: 'none',
    },
    issueItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
        padding: '6px 12px',
        cursor: 'pointer',
        borderTop: `1px solid ${CapeeshColors.Grey200}`,
        '&:hover': {
            backgroundColor: CapeeshColors.Grey50,
        },
    },
    issueIcon: {
        marginTop: 2,
        fontSize: 16,
    },
    errorIcon: {
        color: CapeeshColors.Red,
    },
    warningIcon: {
        color: '#F59E0B',
    },
    infoIcon: {
        color: CapeeshColors.BlueBright,
    },
    issueText: {
        fontFamily: 'Rubik',
        fontSize: 12,
        color: CapeeshColors.Gray900,
        flex: 1,
    },
    noIssues: {
        padding: '8px 12px',
        fontFamily: 'Rubik',
        fontSize: 12,
        color: CapeeshColors.Gray500,
    },
}))

const NativeSpeakerValidationPanel = ({ scenario, nodes, l2Id, onNavigateToNode }) => {
    const classes = useStyles()
    const [expanded, setExpanded] = useState(false)

    const issues = useMemo(
        () => validateNativeSpeakerContent(scenario, nodes, l2Id),
        [scenario, nodes, l2Id]
    )

    const errorCount = issues.filter((i) => i.severity === 'error').length
    const warningCount = issues.filter((i) => i.severity === 'warning').length
    const infoCount = issues.filter((i) => i.severity === 'info').length

    if (issues.length === 0) return null

    const severityIcon = (severity) => {
        switch (severity) {
            case 'error':
                return <ErrorIcon className={`${classes.issueIcon} ${classes.errorIcon}`} />
            case 'warning':
                return <WarningIcon className={`${classes.issueIcon} ${classes.warningIcon}`} />
            case 'info':
                return <InfoIcon className={`${classes.issueIcon} ${classes.infoIcon}`} />
            default:
                return null
        }
    }

    return (
        <div className={classes.root}>
            <div className={classes.header} onClick={() => setExpanded(!expanded)}>
                <div className={classes.headerLeft}>
                    <Typography className={classes.headerTitle}>
                        Native Speaker Check
                    </Typography>
                    {errorCount > 0 && (
                        <span className={`${classes.badge} ${classes.badgeError}`}>
                            {errorCount} error{errorCount !== 1 ? 's' : ''}
                        </span>
                    )}
                    {warningCount > 0 && (
                        <span className={`${classes.badge} ${classes.badgeWarning}`}>
                            {warningCount} warning{warningCount !== 1 ? 's' : ''}
                        </span>
                    )}
                    {infoCount > 0 && (
                        <span className={`${classes.badge} ${classes.badgeInfo}`}>
                            {infoCount} info
                        </span>
                    )}
                </div>
                <IconButton size="small">
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </div>
            <Collapse in={expanded}>
                <ul className={classes.issueList}>
                    {issues.map((issue, idx) => (
                        <li
                            key={idx}
                            className={classes.issueItem}
                            onClick={() => issue.nodeId && onNavigateToNode?.(issue.nodeId)}
                        >
                            {severityIcon(issue.severity)}
                            <Typography className={classes.issueText}>
                                {issue.message}
                            </Typography>
                        </li>
                    ))}
                </ul>
            </Collapse>
        </div>
    )
}

export default NativeSpeakerValidationPanel
